'use strict';
exports.default = exports;

exports.customBetterFormatPercentage = function(percentage) {
  const percentValue = percentage * 100;

  if (percentValue % 1 === 0) {
    return `${percentValue.toFixed(0)}%`;
  }

  if ((percentValue * 1000) % 10 !== 0) {
    return `${percentValue.toFixed(3)}%`;
  } else {
    return `${percentValue.toFixed(2)}%`;
  }
};

/**
 * Fallback do arredondamento por deslocamento de expoente (`num + 'e+2'`) — ver o comentário
 * do `round`. Só é chamado quando aquele truque produz NaN, então nunca altera um resultado
 * que já era válido.
 *
 * @param {*} num entrada original (pode ser string, ±Infinity, NaN, notação exponencial)
 * @param {Number} precision casas decimais
 * @returns {Number} valor arredondado, ou 0 quando a entrada não é um número finito
 */
function roundFallback(num, precision) {
  const n = Number(num);
  if(!isFinite(n)) {
    // ±Infinity e NaN não são valor: devolver 0 mantém a convenção do guard `if(!num)`.
    return 0;
  }
  const factor = Math.pow(10, precision);
  const shifted = n*factor;
  if(!isFinite(shifted)) {
    // Magnitude perto de Number.MAX_VALUE — arredondar não muda nada nessa escala.
    return n;
  }
  // `|| 0` normaliza o -0 que sai de arredondar um resíduo negativo (ex.: -9e-13). -0 compara
  // igual a 0 mas é gravado como -0 e exibido "-R$ 0,00" — mesma normalização do applyTaxCustom.
  return Math.round(shifted)/factor || 0;
}

/**
 * Arredonda deslocando o expoente da representação em string (`+(Math.round(num+'e+2')+'e-2')`),
 * técnica que evita o erro de `Math.round(n*100)/100` em casos como `1.005`.
 *
 * ⚠️ O truque depende de `num` virar string em notação DECIMAL. O JS usa notação EXPONENCIAL
 * para `|n| < 1e-6` e `|n| >= 1e21`, e aí `'1e-13' + 'e+2'` = `'1e-13e+2'` → `Math.round` de
 * NaN → **NaN**. `±Infinity` cai no mesmo buraco. Como o retorno alimenta campos que vão para
 * o Firestore e para o payload das callables (que recusa NaN — `Data cannot be encoded in JSON`),
 * um NaN aqui só aparece muito longe da origem. O caso real: o abatimento pré-nota do encontro
 * de contas consumindo TODO o `valueBaseTransfer` deixa um resíduo binário de ~1e-13 na
 * subtração, e `currency` desse resíduo devolvia NaN em vez de 0, contaminando
 * `valueBaseTransfer` e, na sequência, `valueBaseIR`.
 *
 * O fallback só entra quando o caminho rápido produziria NaN — nenhum arredondamento hoje
 * correto muda de valor.
 */
exports.round = function(num, precision = 2) {
  if(!num) {
    return 0;
  }
  if(!precision) {
    precision = 2;
  }
  const rounded = +(Math.round(num + 'e+'+precision)  + 'e-'+precision);
  if(isNaN(rounded)) {
    return roundFallback(num, precision);
  }
  return rounded;
};

exports.roundNear = function(n, near) {
  if(!n) {
    return 0;
  }
  if(!near) {
    near = 0.5;
  }
  const nn = 1/near;
  return exports.round(n*nn, 0)/nn;
};

exports.ceilNear = function(n, near) {
  if(!n) {
    return 0;
  }
  if(!near) {
    near = 0.5;
  }
  const nn = 1/near;
  return (Math.ceil(n*nn)/nn) ||0;
};

/**
 *
 * @param num
 * @param {boolean} convertComma
 * @returns {number}
 */
exports.currency= function(num, convertComma = false) {
  if(!num) {
    return 0;
  }
  if (process.env.NODE_ENV !== 'production') {
    if (typeof convertComma === 'number') {
      console.warn('O segundo parâmetro de NumberUtils.currency é um booleano, no caso, se deve ser convertida a vírgula em ponto');
    }
  }
  if(convertComma && typeof num==='string') {
    num = num.replace(/[,]/g, '.');
  }
  const rounded = +(Math.round(num + 'e+2')  + 'e-2');
  if(isNaN(rounded)) {
    // Mesmo buraco do `round`: notação exponencial (|n| < 1e-6 ou >= 1e21) e ±Infinity.
    // Devolver NaN daqui é o pior desfecho possível — ele atravessa o cálculo em silêncio
    // (só quem passa por outro `currency` é saneado, porque `currency(NaN)` é 0) e só estoura
    // no serializer da callable, com o payload inteiro e sem o campo de origem.
    return roundFallback(num, 2);
  }
  return rounded;
};

/**
 * Parses a currency string into a numeric value, handling both Brazilian (BR)
 * and US formatting conventions.
 *
 * BR format: dot = thousands separator, comma = decimal separator (e.g. "6.500,00")
 * US format: comma = thousands separator, dot = decimal separator (e.g. "6,500.00")
 *
 * Numeric inputs are passed through unchanged (XLSX pre-parsed values).
 *
 * @param {string|number|null|undefined} value - The raw value to parse
 * @param {string} format - Currency format: 'BR' or 'US' (default 'US')
 * @returns {number} Parsed numeric value, rounded to 2 decimal places
 */
exports.parseCurrencyValue = function(value, format) {
  if (value == null || value === '') return 0;

  format = (format || 'US').toUpperCase();

  // If already a number, pass through (XLSX already parsed it)
  if (typeof value === 'number') {
    return isNaN(value) ? 0 : value;
  }

  if (typeof value !== 'string') return 0;

  // Remove currency symbols and whitespace
  let cleaned = value.replace(/[R$\s]/gi, '').trim();

  if (!cleaned) return 0;

  if (format === 'BR') {
    // BR: dot = thousands separator, comma = decimal
    // Strip all dots (thousands), then replace comma with dot (decimal)
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    // US: comma = thousands separator, dot = decimal
    // Strip all commas (thousands)
    cleaned = cleaned.replace(/,/g, '');
  }

  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : +(Math.round(num + 'e+2') + 'e-2');
};

exports.parseFloat= function(value, defaultValue=0) {
  if(!value) {
    return defaultValue;
  }
  return parseFloat(value) || defaultValue;
};

exports.parseInt= function(value, defaultValue=0) {
  if(!value) {
    return defaultValue;
  }
  return parseInt(value) || defaultValue;
};

exports.module= function(value) {
  if (typeof value !== 'number') {
    return 0;
  }
  return  value < 0 ? (value * -1) : value;
};

